import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton, SkeletonButton, SkeletonText } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/** Label widths, staggered so a stack of fields doesn't read as a ladder. */
const LABEL_WIDTHS = ['96px', '124px', '82px', '140px', '108px', '90px'] as const;

interface FormSkeletonProps {
    readonly fields?: number;
    /** Index of a field rendered as a textarea rather than a single-line input. */
    readonly textareaAt?: number;
    /** Draws a Save/Cancel row at the bottom. */
    readonly showActions?: boolean;
    readonly className?: string;
}

/**
 * Loading placeholder for a form: label + control pairs at the shared control
 * height (36px), optionally with a textarea and an action row.
 *
 * Controls are drawn as filled shapes rather than empty bordered boxes: unlike
 * a search field, a form input in a loading state is about to receive a *value*,
 * so a filled placeholder is the honest representation.
 */
export function FormSkeleton({
    fields = 4,
    textareaAt,
    showActions = false,
    className,
}: FormSkeletonProps) {
    return (
        <div className={cn('flex flex-col gap-5', className)}>
            {Array.from({ length: fields }).map((_, i) => (
                <div key={i} className="flex flex-col gap-1.5">
                    <SkeletonText size="xs" width={LABEL_WIDTHS[i % LABEL_WIDTHS.length]} />
                    {i === textareaAt ? (
                        <Skeleton className="h-24 w-full rounded-md" />
                    ) : (
                        <Skeleton className="h-9 w-full rounded-md" />
                    )}
                </div>
            ))}
            {showActions && (
                <div className="flex items-center gap-2 pt-1">
                    <SkeletonButton width={96} />
                    <SkeletonButton width={84} />
                </div>
            )}
        </div>
    );
}

interface DetailPanelSkeletonProps {
    /** Number of label/value pairs in the panel. */
    readonly rows?: number;
    readonly showHeader?: boolean;
    readonly titleWidth?: string;
    readonly className?: string;
}

const VALUE_WIDTHS = ['64%', '42%', '78%', '52%', '70%', '38%'] as const;

/**
 * Loading placeholder for a read-only detail panel — the label/value stacks
 * behind record detail pages, execution drawers and side rails.
 */
export function DetailPanelSkeleton({
    rows = 6,
    showHeader = true,
    titleWidth = '150px',
    className,
}: DetailPanelSkeletonProps) {
    return (
        <Card className={className}>
            {showHeader && (
                <CardHeader>
                    <SkeletonText size="lg" width={titleWidth} />
                </CardHeader>
            )}
            <CardContent className="flex flex-col gap-4">
                {Array.from({ length: rows }).map((_, i) => (
                    <div key={i} className="flex items-center justify-between gap-4">
                        <SkeletonText size="xs" width={LABEL_WIDTHS[i % LABEL_WIDTHS.length]} />
                        <SkeletonText
                            size="sm"
                            width={VALUE_WIDTHS[i % VALUE_WIDTHS.length]}
                            className="max-w-[60%] flex-1 justify-end"
                        />
                    </div>
                ))}
            </CardContent>
        </Card>
    );
}
